import { useTranslation } from "react-i18next";
import { Button } from "@core/components";
import { Icon } from "@core/assets";

/**
 * "Nothing open right now" — the catalogue's only empty state.
 *
 * One state, not two: the public endpoint takes no filters, so an empty response can't mean
 * "your search matched nothing". It has exactly one meaning, and the copy says it plainly rather
 * than implying the visitor did something wrong.
 */
export function PublicRoundsEmptyState() {
  const { t } = useTranslation("acquire");

  return (
    <div className="flex flex-col items-center gap-md py-2xl text-center">
      <span className="grid size-24 place-items-center rounded-full bg-brand-50 text-(--text-link)">
        <Icon name="flag" size={40} />
      </span>

      <div className="flex max-w-100 flex-col gap-xs">
        <h2 className="text-lg-semibold text-(--text-primary)">
          {t("public.empty.title")}
        </h2>
        <p className="text-sm-regular text-(--text-secondary)">
          {t("public.empty.description")}
        </p>
      </div>
    </div>
  );
}

/**
 * The catalogue failed to load.
 *
 * A VISIBLE error branch with a retry, where the staff screens rely on a toast: a toast assumes
 * someone is watching a screen they already trust. An anonymous visitor arriving on an empty page
 * has nothing to distinguish "the platform is down" from "there are no rounds", and no reason to
 * assume the former — so the page has to say which, and offer the retry itself.
 *
 * The message is generic on purpose. The taxonomy's per-kind copy is written for authenticated
 * users ("your session expired", "you don't have access"), none of which can apply to an
 * unauthenticated read, and a `429` or `503` here is transient — retry is the whole remedy.
 */
export function PublicRoundsError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation("acquire");
  const { t: tCommon } = useTranslation("common");

  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-md py-2xl text-center"
    >
      <span className="grid size-24 place-items-center rounded-full bg-error-50 text-(--feedback-error-icon)">
        <Icon name="alert" size={40} />
      </span>

      <div className="flex max-w-100 flex-col gap-xs">
        <h2 className="text-lg-semibold text-(--text-primary)">
          {t("public.error.title")}
        </h2>
        <p className="text-sm-regular text-(--text-secondary)">
          {t("public.error.description")}
        </p>
      </div>

      <Button variant="secondary" onPress={onRetry}>
        {tCommon("actions.retry")}
      </Button>
    </div>
  );
}
